import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Paper, Stack, Typography, TextField, Button, Chip, Divider, Grid,
  IconButton, Alert,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Swal from "sweetalert2";
import { obterRecurso, analisarRecurso } from "../../services/recursoService";
import { baixarAnexo } from "../../services/anexoService";

const fmt = (d) => (d ? new Date(d).toLocaleString("pt-BR") : "—");
const chip = (s) =>
  s === "D" ? <Chip size="small" label="Deferido" color="success" />
  : s === "I" ? <Chip size="small" label="Indeferido" color="error" />
  : <Chip size="small" label="Pendente" color="warning" />;

export default function RecursoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [parecer, setParecer] = useState("");

  const carregar = async () => {
    const { data } = await obterRecurso(id);
    setR(data);
    setParecer(data.ParecerAnalista || "");
  };
  useEffect(() => { carregar().catch((e) => Swal.fire("Erro", e?.response?.data || e.message, "error")); }, [id]);

  const decidir = async (status) => {
    const label = status === "D" ? "deferir" : "indeferir";
    const conf = await Swal.fire({
      title: `Confirmar ${label}?`, icon: "question",
      showCancelButton: true, confirmButtonText: "Confirmar", cancelButtonText: "Cancelar",
    });
    if (!conf.isConfirmed) return;
    try {
      await analisarRecurso(id, { Status: status, Parecer: parecer });
      await carregar();
      Swal.fire("Pronto", "Recurso atualizado.", "success");
    } catch (e) { Swal.fire("Erro", e?.response?.data || e.message, "error"); }
  };

  if (!r) return <Typography>Carregando…</Typography>;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Recurso #{r.Id} {chip(r.Status)}</Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={() => navigate("/recursos")}>Voltar</Button>
          <Button variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => navigate(`/fichas/${r.IdFicha}`)}>
            Abrir ficha #{r.IdFicha}
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, sm: 4 }}><Typography variant="caption" color="text.secondary">Candidato</Typography><Typography>{r.Candidato || "—"}</Typography></Grid>
          <Grid size={{ xs: 12, sm: 4 }}><Typography variant="caption" color="text.secondary">Edição</Typography><Typography>{r.Edicao || "—"}</Typography></Grid>
          <Grid size={{ xs: 12, sm: 4 }}><Typography variant="caption" color="text.secondary">Enviado em</Typography><Typography>{fmt(r.DtCadastro)}</Typography></Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="caption" color="text.secondary">Descrição do recurso</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}>{r.Descricao || "—"}</Typography>
          </Grid>
        </Grid>

        <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Anexos</Typography>
        <Divider sx={{ mb: 1, mt: 0.5 }} />
        {(r.Anexos || []).length === 0 && <Typography variant="body2" color="text.secondary">Nenhum anexo.</Typography>}
        {(r.Anexos || []).map((a) => (
          <Stack key={a.Id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
            <Typography variant="body2" sx={{ flex: 1, wordBreak: "break-all" }}>{a.NomeOriginal}</Typography>
            <IconButton size="small" onClick={() => baixarAnexo(a.Id, a.NomeOriginal)}><DownloadIcon fontSize="small" /></IconButton>
          </Stack>
        ))}
      </Paper>

      <Paper sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Parecer do analista</Typography>
        {r.Status !== "P" && (
          <Alert severity={r.Status === "D" ? "success" : "error"} sx={{ mb: 2 }}>
            {r.Status === "D" ? "Deferido" : "Indeferido"} em {fmt(r.DtAnalise)}{r.UsuarioAnalise ? ` por ${r.UsuarioAnalise}` : ""}.
          </Alert>
        )}
        <TextField fullWidth multiline minRows={3} label="Parecer" value={parecer}
          onChange={(e) => setParecer(e.target.value)} sx={{ mb: 2 }} />
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button color="error" variant="outlined" onClick={() => decidir("I")}>Indeferir</Button>
          <Button color="success" variant="contained" onClick={() => decidir("D")}>Deferir</Button>
        </Stack>
      </Paper>
    </Box>
  );
}
